import React, { useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {
  FiCheck, FiX, FiZap, FiStar, FiMusic, FiVideo,
  FiDownload, FiMessageCircle, FiEye, FiGift, FiShield, FiHeadphones
} = FiIcons;

const PERK_ICONS = {
  'exclusive': FiEye,
  'download': FiDownload,
  'video': FiVideo,
  'chat': FiMessageCircle,
  'stems': FiMusic,
  'merch': FiGift,
  'early': FiZap,
  'badge': FiShield,
  'audio': FiHeadphones,
  'default': FiStar,
};

const getPerkIcon = (perk) => {
  const lower = perk.toLowerCase();
  for (const [key, icon] of Object.entries(PERK_ICONS)) {
    if (lower.includes(key)) return icon;
  }
  return PERK_ICONS.default;
};

const TierBadge = ({ color, name }) => (
  <div className="flex items-center gap-2">
    <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: color || '#3b82f6' }} />
    <span className="text-xs font-black uppercase tracking-widest text-zinc-400">{name}</span>
  </div>
);

/* ── Comparison Table ───────────────────────────────────────── */
const ComparisonTable = ({ tiers, activeTierId, onSelectTier, userId }) => {
  const allPerks = [...new Set(tiers.flatMap(t => t.perks || []))];

  return (
    <div className="overflow-x-auto rounded-3xl border border-zinc-800">
      <table className="w-full min-w-[600px]">
        <thead>
          <tr className="border-b border-zinc-800">
            <th className="text-left p-6 text-xs font-black uppercase tracking-widest text-zinc-600 w-48">
              Benefits
            </th>
            {tiers.map((tier, i) => {
              const isActive = activeTierId === tier.id;
              const isMid = i === Math.floor(tiers.length / 2) && tiers.length > 1;
              return (
                <th key={tier.id} className={`p-6 text-center relative ${isMid ? 'bg-white/5' : ''}`}>
                  {isMid && !isActive && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 bg-white text-black text-[10px] font-black uppercase tracking-widest rounded-full whitespace-nowrap">
                      Popular
                    </div>
                  )}
                  {isActive && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 bg-green-500 text-black text-[10px] font-black uppercase tracking-widest rounded-full whitespace-nowrap">
                      Your Plan
                    </div>
                  )}
                  <div className="space-y-2">
                    <div className="w-3 h-3 rounded-full mx-auto" style={{ backgroundColor: tier.color || '#3b82f6' }} />
                    <p className="text-white font-black uppercase tracking-tight text-sm">{tier.name}</p>
                    <p className="text-2xl font-black text-white">${tier.price}<span className="text-xs text-zinc-500 font-bold">/mo</span></p>
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {allPerks.map((perk, pi) => (
            <tr key={pi} className={`border-b border-zinc-900/50 ${pi % 2 === 0 ? 'bg-zinc-950' : 'bg-zinc-900/20'}`}>
              <td className="p-5 pl-6">
                <div className="flex items-center gap-3">
                  <SafeIcon icon={getPerkIcon(perk)} className="text-zinc-600 text-sm flex-shrink-0" />
                  <span className="text-sm font-bold text-zinc-300">{perk}</span>
                </div>
              </td>
              {tiers.map((tier, ti) => {
                const hasPerk = tier.perks?.includes(perk);
                const isMid = ti === Math.floor(tiers.length / 2) && tiers.length > 1;
                return (
                  <td key={tier.id} className={`p-5 text-center ${isMid ? 'bg-white/[0.02]' : ''}`}>
                    {hasPerk
                      ? <SafeIcon icon={FiCheck} className="text-green-400 text-lg mx-auto block" />
                      : <SafeIcon icon={FiX} className="text-zinc-800 text-lg mx-auto block" />
                    }
                  </td>
                );
              })}
            </tr>
          ))}
          <tr className="bg-zinc-900/30">
            <td className="p-6" />
            {tiers.map((tier, i) => {
              const isActive = activeTierId === tier.id;
              const isMid = i === Math.floor(tiers.length / 2) && tiers.length > 1;
              return (
                <td key={tier.id} className={`p-6 text-center ${isMid ? 'bg-white/[0.02]' : ''}`}>
                  <button
                    onClick={() => !isActive && userId && onSelectTier(tier)}
                    disabled={isActive || !userId}
                    className={`px-6 py-3 rounded-2xl text-xs font-black uppercase tracking-widest transition-all ${
                      isActive
                        ? 'bg-green-500/20 text-green-400 border border-green-500/30 cursor-default'
                        : !userId
                        ? 'bg-zinc-800 text-zinc-600 cursor-not-allowed'
                        : isMid
                        ? 'bg-white text-black hover:scale-105 active:scale-95 shadow-lg'
                        : 'border border-zinc-700 text-zinc-300 hover:border-white hover:text-white'
                    }`}>
                    {isActive ? '✓ Active' : !userId ? 'Sign In' : `$${tier.price}/mo`}
                  </button>
                </td>
              );
            })}
          </tr>
        </tbody>
      </table>
    </div>
  );
};

/* ── Perk Highlight Cards ───────────────────────────────────── */
const PerkHighlights = ({ tiers }) => {
  const topTier = tiers[tiers.length - 1];
  const allPerks = [...new Set(tiers.flatMap(t => t.perks || []))];

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <span className="text-xs font-black tracking-widest text-zinc-600 uppercase">What You Get</span>
        <h3 className="text-2xl font-black tracking-tighter text-white uppercase">All Benefits</h3>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {allPerks.map((perk, i) => {
          const availableIn = tiers.filter(t => t.perks?.includes(perk));
          const lowestTier = availableIn[0];
          return (
            <div key={i} className="flex items-start gap-4 p-5 bg-zinc-900/40 border border-zinc-800/50 rounded-2xl hover:border-zinc-700 transition-all">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: `${lowestTier?.color || '#3b82f6'}20` }}>
                <SafeIcon icon={getPerkIcon(perk)}
                  className="text-lg" style={{ color: lowestTier?.color || '#3b82f6' }} />
              </div>
              <div className="flex-1 min-w-0 space-y-1">
                <p className="text-sm font-black text-white">{perk}</p>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-black uppercase tracking-widest text-zinc-600">From</span>
                  <TierBadge color={lowestTier?.color} name={lowestTier?.name} />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ── Tier Selector Pills ────────────────────────────────────── */
const TierPills = ({ tiers, selected, onSelect }) => (
  <div className="flex flex-wrap gap-2">
    <button
      onClick={() => onSelect(null)}
      className={`px-5 py-2.5 rounded-full text-xs font-black uppercase tracking-widest transition-all ${
        selected === null ? 'bg-white text-black' : 'bg-zinc-900 border border-zinc-800 text-zinc-400 hover:border-zinc-600'
      }`}>
      Compare All
    </button>
    {tiers.map(tier => (
      <button key={tier.id}
        onClick={() => onSelect(tier.id)}
        className={`px-5 py-2.5 rounded-full text-xs font-black uppercase tracking-widest transition-all flex items-center gap-2 ${
          selected === tier.id ? 'text-black' : 'bg-zinc-900 border border-zinc-800 text-zinc-400 hover:border-zinc-600'
        }`}
        style={selected === tier.id ? { backgroundColor: tier.color || '#3b82f6' } : {}}>
        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: selected === tier.id ? '#000' : (tier.color || '#3b82f6') }} />
        {tier.name}
      </button>
    ))}
  </div>
);

/* ── Single Tier Detail ─────────────────────────────────────── */
const TierDetail = ({ tier, isSubscribed, onSubscribe, userId }) => {
  const [loading, setLoading] = useState(false);

  const handleClick = async () => {
    if (!userId || isSubscribed || loading) return;
    setLoading(true);
    await onSubscribe({ tierId: tier.id, amount: tier.price });
    setLoading(false);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 p-8 bg-zinc-900/40 border border-zinc-800 rounded-3xl">
      <div className="space-y-6">
        <div className="space-y-3">
          <TierBadge color={tier.color} name="Tier" />
          <h3 className="text-5xl font-black tracking-tighter text-white uppercase leading-none">{tier.name}</h3>
          <div className="flex items-end gap-2">
            <span className="text-5xl font-black text-white">${tier.price}</span>
            <span className="text-zinc-500 font-bold text-sm pb-1">/month</span>
          </div>
          {tier.description && (
            <p className="text-zinc-300 text-base leading-relaxed font-bold">{tier.description}</p>
          )}
        </div>
        <button onClick={handleClick} disabled={isSubscribed || loading || !userId}
          className={`px-10 py-5 rounded-2xl text-xs font-black uppercase tracking-widest transition-all ${
            isSubscribed ? 'bg-green-500/20 text-green-400 border border-green-500/30 cursor-default'
            : !userId ? 'bg-zinc-800 text-zinc-600 cursor-not-allowed'
            : 'bg-white text-black hover:scale-105 active:scale-95 shadow-xl'
          }`}>
          {loading ? 'Processing...' : isSubscribed ? '✓ Subscribed' : !userId ? 'Sign In to Subscribe' : `Join · $${tier.price}/mo`}
        </button>
      </div>

      <div className="space-y-4">
        <p className="text-xs font-black tracking-widest text-zinc-600 uppercase">What's included</p>
        <ul className="space-y-3">
          {(tier.perks || []).map((perk, i) => (
            <li key={i} className="flex items-start gap-4 p-4 bg-zinc-950/50 rounded-2xl">
              <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: `${tier.color || '#3b82f6'}20` }}>
                <SafeIcon icon={getPerkIcon(perk)} className="text-sm" style={{ color: tier.color || '#3b82f6' }} />
              </div>
              <span className="text-sm font-black text-zinc-200 mt-1.5">{perk}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

/* ── Main Component ─────────────────────────────────────────── */
const TierBenefitsOverview = ({ tiers, artistId, userId, activeTierId, onSubscribe }) => {
  const [view, setView] = useState('compare');
  const [selectedTierId, setSelectedTierId] = useState(null);

  if (!tiers?.length) return null;

  const selectedTier = tiers.find(t => t.id === selectedTierId);
  const isSubscribed = (tierId) => activeTierId === tierId;

  return (
    <div className="space-y-10">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 border-b border-zinc-900 pb-8">
        <div className="space-y-2">
          <span className="text-xs font-black tracking-widest text-blue-500 uppercase">Membership</span>
          <h2 className="text-5xl font-black tracking-tighter text-white uppercase leading-none">
            Tier Benefits
          </h2>
          <p className="text-zinc-400 font-bold text-base">
            {tiers.length} membership tier{tiers.length > 1 ? 's' : ''} · From ${Math.min(...tiers.map(t => t.price))}/mo
          </p>
        </div>

        {/* View Toggle */}
        <div className="flex gap-2 bg-zinc-900 border border-zinc-800 rounded-2xl p-1.5 self-start sm:self-auto">
          {['compare', 'highlights'].map(v => (
            <button key={v} onClick={() => setView(v)}
              className={`px-5 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${
                view === v ? 'bg-white text-black' : 'text-zinc-500 hover:text-zinc-300'
              }`}>
              {v === 'compare' ? '⊞ Compare' : '✦ Highlights'}
            </button>
          ))}
        </div>
      </div>

      {/* Active Membership Banner */}
      {activeTierId && (
        <div className="p-5 bg-green-500/10 border border-green-500/30 rounded-2xl flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center text-green-400 text-lg font-black">✓</div>
            <div>
              <p className="text-xs font-black tracking-widest text-green-400 uppercase">Active Membership</p>
              <p className="text-white font-black">{tiers.find(t => t.id === activeTierId)?.name} · ${tiers.find(t => t.id === activeTierId)?.price}/mo</p>
            </div>
          </div>
          <span className="text-xs font-black text-green-400/60 uppercase tracking-widest">Renews Monthly</span>
        </div>
      )}

      {view === 'compare' && (
        <div className="space-y-6">
          <TierPills tiers={tiers} selected={selectedTierId} onSelect={setSelectedTierId} />
          {selectedTier ? (
            <TierDetail
              tier={selectedTier}
              isSubscribed={isSubscribed(selectedTier.id)}
              onSubscribe={onSubscribe}
              userId={userId}
            />
          ) : (
            <ComparisonTable
              tiers={tiers}
              activeTierId={activeTierId}
              onSelectTier={(tier) => onSubscribe({ tierId: tier.id, amount: tier.price })}
              userId={userId}
            />
          )}
        </div>
      )}

      {view === 'highlights' && <PerkHighlights tiers={tiers} />}

      {/* Upsell CTA */}
      {!activeTierId && (
        <div className="relative overflow-hidden p-8 rounded-3xl border border-zinc-800 bg-gradient-to-br from-zinc-900 to-zinc-950 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="absolute inset-0 opacity-5">
            <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500 rounded-full blur-3xl" />
          </div>
          <div className="relative space-y-2 text-center sm:text-left">
            <p className="text-xs font-black tracking-widest text-zinc-500 uppercase">Ready to support?</p>
            <p className="text-2xl font-black text-white tracking-tighter">Join from <span className="text-blue-400">${Math.min(...tiers.map(t => t.price))}/month</span></p>
            <p className="text-zinc-400 font-bold text-sm">Cancel anytime · 100% goes to the artist</p>
          </div>
          <button
            onClick={() => {
              const cheapest = tiers.reduce((a, b) => a.price < b.price ? a : b);
              onSubscribe({ tierId: cheapest.id, amount: cheapest.price });
            }}
            disabled={!userId}
            className="relative px-10 py-5 bg-white text-black font-black uppercase tracking-widest text-xs rounded-2xl hover:scale-105 active:scale-95 transition-all shadow-xl disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap">
            {userId ? 'Become a Member' : 'Sign In to Join'}
          </button>
        </div>
      )}
    </div>
  );
};

export default TierBenefitsOverview;